/**
 * Managed Agents — the PPTX deck template card (`/client-reports/agents`).
 *
 * Says which template a run would use RIGHT NOW — this brand's own upload,
 * the agency-wide default, or none (decks fall back to the report branding
 * tokens) — and offers upload / replace / remove for BOTH scopes, so the
 * fallback is never invisible while managing a brand.
 *
 * Uploads follow the ReportBrandingPage hidden-input+label multipart pattern:
 * field name `template`, `.pptx/.potx` only. The server is authoritative on
 * validity (real ZIP, ≥1 slide, size cap) and answers 422
 * `invalid_template` / `template_too_large` — surfaced INLINE here, not as a
 * toast, so the message sits next to the button that caused it.
 *
 * Reading is `portal.reports.review` (the shop GET also carries the agency
 * default, so review-only analysts see the full picture without touching the
 * settings-gated agency endpoints); every write is `portal.settings.manage`.
 * Active agents' deployments are re-pointed server-side on upload — runs
 * already made keep the template version they were stamped with.
 */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import Popup from '../../../ui/popup/Popup';
import { setMessage } from '../../../store/reducers/appSlice';
import {
  deleteAgencyTemplate,
  deleteTemplate,
  getTemplate,
  uploadAgencyTemplate,
  uploadTemplate,
  type ShopTemplateState,
  type TemplateView,
} from '../../../lib/api/client-reports/client-agents-api';
import { formatRunMoment, type Translate } from './agent-helpers';

type TemplateScope = 'client' | 'agency';

interface AgentTemplateCardProps {
  slug: string;
  canManage: boolean;
  /** The page re-fetches its agents after a change (AgentView.template refs). */
  onTemplateChanged: () => void;
}

/** 422 payloads → the inline sentence next to the upload button. */
function friendlyTemplateError(err: any, t: Translate): string {
  if (err?.status === 422) {
    if (err.code === 'template_too_large') {
      return (
        err.message ||
        t(
          'AgentTemplateTooLarge',
          'That file is too large for a template — the cap is 20MB.'
        )
      );
    }
    if (err.code === 'invalid_template') {
      return (
        err.message ||
        t(
          'AgentTemplateInvalid',
          'That file is not a valid PowerPoint template — upload a .pptx or .potx with at least one slide.'
        )
      );
    }
    return (
      err?.validation?.template?.[0] ||
      err?.message ||
      t(
        'AgentTemplateRejected',
        'That file was rejected — upload a .pptx or .potx template.'
      )
    );
  }
  return (
    err?.message ||
    t('AgentTemplateUploadFailed', 'The upload failed — try again.')
  );
}

function templateMeta(tpl: TemplateView, t: Translate): string {
  const parts = [`v${tpl.version}`];
  if (tpl.uploaded_by_name) {
    parts.push(
      t('AgentTemplateUploadedBy', 'uploaded by {{name}}', {
        name: tpl.uploaded_by_name,
      })
    );
  }
  const when = formatRunMoment(tpl.uploaded_at, undefined, t);
  if (when) parts.push(when);
  return parts.join(' · ');
}

const AgentTemplateCard = ({
  slug,
  canManage,
  onTemplateChanged,
}: AgentTemplateCardProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [state, setState] = useState<ShopTemplateState | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<TemplateScope | null>(null);
  const [removing, setRemoving] = useState(false);
  /** Scope awaiting remove confirmation, or null. */
  const [confirmRemove, setConfirmRemove] = useState<TemplateScope | null>(
    null
  );
  /** Inline 422 message, rendered next to the controls that caused it. */
  const [uploadError, setUploadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setLoadError(null);
    try {
      setState(await getTemplate(slug));
    } catch (err: any) {
      setState(null);
      setLoadError(
        err?.message ||
          t('AgentTemplateLoadFailed', 'Failed to load the template state.')
      );
    } finally {
      setLoading(false);
    }
    // `t` is left out: a language switch must not re-fetch the template.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    setUploadError(null);
    load();
  }, [load]);

  const toast = (message: string, messageType: 'success' | 'error') =>
    dispatch(setMessage({ message, messageType }));

  const handlePick = async (scope: TemplateScope, file: File | undefined) => {
    if (!file || uploading || !canManage) return;
    setUploadError(null);
    setUploading(scope);
    try {
      if (scope === 'client') await uploadTemplate(slug, file);
      else await uploadAgencyTemplate(file);
      await load();
      onTemplateChanged();
      toast(
        scope === 'client'
          ? t(
              'AgentTemplateUploadedBrand',
              'Template uploaded — future runs for this brand use {{file}}.',
              { file: file.name }
            )
          : t(
              'AgentTemplateUploadedAgency',
              'Agency default uploaded — brands without their own template use {{file}}.',
              { file: file.name }
            ),
        'success'
      );
    } catch (err: any) {
      setUploadError(friendlyTemplateError(err, t));
    } finally {
      setUploading(null);
    }
  };

  const handleRemove = async () => {
    const scope = confirmRemove;
    if (!scope || removing) return;
    setRemoving(true);
    try {
      if (scope === 'client') await deleteTemplate(slug);
      else await deleteAgencyTemplate();
      await load();
      onTemplateChanged();
      setConfirmRemove(null);
      toast(
        scope === 'client'
          ? state?.agency_default
            ? t(
                'AgentTemplateRemovedBrandToAgency',
                'Brand template removed — runs fall back to the agency default.'
              )
            : t(
                'AgentTemplateRemovedBrandToBranding',
                'Brand template removed — decks are styled from your report branding instead.'
              )
          : t(
              'AgentTemplateRemovedAgency',
              'Agency default removed — brands without their own template are styled from their report branding.'
            ),
        'success'
      );
    } catch (err: any) {
      toast(
        err?.message ||
          t('AgentTemplateRemoveFailed', 'Failed to remove the template.'),
        'error'
      );
    } finally {
      setRemoving(false);
    }
  };

  const current = state?.current ?? null;
  const agencyDefault = state?.agency_default ?? null;
  const effective = state?.effective ?? null;
  const versions = Array.isArray(state?.versions) ? state!.versions : [];
  const previousVersions = versions.filter((v) => v.id !== current?.id);

  return (
    <section className='client-agents__card'>
      <div className='client-agents__card-head'>
        <div className='client-agents__card-id'>
          <h3 className='client-agents__card-title'>
            {t('AgentTemplateTitle', 'PPTX deck template')}
          </h3>
        </div>
        {canManage && !loading && !loadError && (
          <label className='client-agents__file-btn'>
            {uploading === 'client'
              ? t('AgentTemplateUploading', 'Uploading…')
              : current
              ? t('AgentTemplateReplace', 'Replace template')
              : t('AgentTemplateUpload', 'Upload template')}
            <input
              type='file'
              accept='.pptx,.potx'
              hidden
              disabled={uploading !== null}
              onChange={(e) => {
                const file = e.target.files?.[0];
                // Reset so picking the same file again re-fires onChange.
                e.target.value = '';
                handlePick('client', file);
              }}
            />
          </label>
        )}
      </div>
      <p className='client-agents__card-hint'>
        {t(
          'AgentTemplateHint',
          'The PowerPoint file agent-made decks are built on. Placeholders and slide masters come from this file; the numbers come from the run.'
        )}
      </p>

      {loading ? (
        <p className='client-agents__muted'>
          {t('AgentTemplateLoading', 'Loading template…')}
        </p>
      ) : loadError ? (
        <div>
          <p className='client-agents__validation'>{loadError}</p>
          <div style={{ marginTop: '1rem' }}>
            <MainSquareButton
              type='button'
              value={t('Retry', 'Retry')}
              buttonType='light'
              normal
              onClick={load}
            />
          </div>
        </div>
      ) : (
        <>
          {effective === 'client' && current ? (
            <div className='client-agents__tpl-current'>
              <span className='client-agents__tpl-name'>
                {current.filename}
              </span>
              <span className='client-agents__tpl-meta'>
                {templateMeta(current, t)}
              </span>
              <span className='client-agents__hint'>
                {t(
                  'AgentTemplateBrandOwn',
                  "This brand's own template — it wins over the agency default."
                )}
              </span>
            </div>
          ) : effective === 'agency' && agencyDefault ? (
            <div className='client-agents__tpl-current'>
              <span className='client-agents__tpl-name'>
                {agencyDefault.filename}
              </span>
              <span className='client-agents__tpl-meta'>
                {templateMeta(agencyDefault, t)}
              </span>
              <span className='client-agents__hint'>
                {t(
                  'AgentTemplateUsingAgency',
                  'Using the agency-wide default — upload a template above to override it for this brand only.'
                )}
              </span>
            </div>
          ) : (
            <div className='client-agents__tpl-current'>
              <span className='client-agents__tpl-meta'>
                {t(
                  'AgentTemplateNone',
                  'No template yet — decks are styled from your report branding (colours, logo, fonts). Upload a .pptx or .potx to control the exact deck design.'
                )}
              </span>
            </div>
          )}

          {uploadError && (
            <p className='client-agents__validation' role='alert'>
              {uploadError}
            </p>
          )}

          {canManage && current && (
            <div className='client-agents__tpl-actions'>
              <button
                type='button'
                className='client-agents__rowbtn client-agents__rowbtn--danger'
                disabled={uploading !== null || removing}
                onClick={() => setConfirmRemove('client')}
              >
                {t('AgentTemplateRemoveBrand', "Remove this brand's template")}
              </button>
            </div>
          )}

          {previousVersions.length > 0 && (
            <details className='client-agents__tpl-versions'>
              <summary>
                {t(
                  'AgentTemplatePreviousVersions',
                  'Previous versions ({{count}})',
                  {
                    count: previousVersions.length,
                  }
                )}
              </summary>
              <ul>
                {previousVersions.map((v) => (
                  <li key={v.id}>
                    v{v.version} — {v.filename}
                    {v.uploaded_by_name ? ` · ${v.uploaded_by_name}` : ''}
                    {formatRunMoment(v.uploaded_at, undefined, t)
                      ? ` · ${formatRunMoment(v.uploaded_at, undefined, t)}`
                      : ''}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {/* ---- agency-wide default ---- */}
          <div className='client-agents__tpl-agency'>
            <div className='client-agents__tpl-agency-text'>
              <span className='client-agents__fact-label'>
                {t('AgentTemplateAgencyDefault', 'Agency-wide default')}
              </span>
              {agencyDefault ? (
                <span className='client-agents__tpl-meta'>
                  {agencyDefault.filename} · {templateMeta(agencyDefault, t)}
                </span>
              ) : (
                <span className='client-agents__tpl-meta'>
                  {t('AgentTemplateNoneYet', 'None yet.')}
                </span>
              )}
              <span className='client-agents__hint'>
                {t(
                  'AgentTemplateAgencyHint',
                  'Used by every brand that has no template of its own.'
                )}
              </span>
            </div>
            {canManage && (
              <div className='client-agents__tpl-actions'>
                <label className='client-agents__file-btn'>
                  {uploading === 'agency'
                    ? t('AgentTemplateUploading', 'Uploading…')
                    : agencyDefault
                    ? t('AgentTemplateReplaceDefault', 'Replace default')
                    : t('AgentTemplateUploadDefault', 'Upload default')}
                  <input
                    type='file'
                    accept='.pptx,.potx'
                    hidden
                    disabled={uploading !== null}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      handlePick('agency', file);
                    }}
                  />
                </label>
                {agencyDefault && (
                  <button
                    type='button'
                    className='client-agents__rowbtn client-agents__rowbtn--danger'
                    disabled={uploading !== null || removing}
                    onClick={() => setConfirmRemove('agency')}
                  >
                    {t('Remove', 'Remove')}
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* ---- remove confirmation ---- */}
      <Popup
        trigger={confirmRemove !== null}
        setTrigger={() => {
          if (!removing) setConfirmRemove(null);
        }}
      >
        {confirmRemove && (
          <div className='client-agents__confirm'>
            <h3 className='client-agents__confirm-title'>
              {confirmRemove === 'client'
                ? t(
                    'AgentTemplateConfirmBrandTitle',
                    "Remove this brand's template?"
                  )
                : t(
                    'AgentTemplateConfirmAgencyTitle',
                    'Remove the agency default template?'
                  )}
            </h3>
            <p className='client-agents__confirm-body'>
              {confirmRemove === 'client'
                ? agencyDefault
                  ? t(
                      'AgentTemplateConfirmBrandToAgency',
                      'Future runs for this brand fall back to the agency default ({{file}}). Reports already generated keep the design they were made with.',
                      { file: agencyDefault.filename }
                    )
                  : t(
                      'AgentTemplateConfirmBrandToBranding',
                      'Future runs for this brand are styled from your report branding instead. Reports already generated keep the design they were made with.'
                    )
                : t(
                    'AgentTemplateConfirmAgency',
                    'Brands without their own template are styled from their report branding instead. Brands with their own template are unaffected.'
                  )}
            </p>
            <div className='client-agents__confirm-actions'>
              <MainSquareButton
                type='button'
                value={t('Cancel', 'Cancel')}
                buttonType='light'
                normal
                disabled={removing}
                onClick={() => setConfirmRemove(null)}
              />
              <MainSquareButton
                type='button'
                value={
                  removing
                    ? t('AgentTemplateRemoving', 'Removing…')
                    : t('AgentTemplateRemove', 'Remove template')
                }
                buttonType='danger'
                normal
                loading={removing}
                disabled={removing}
                onClick={handleRemove}
              />
            </div>
          </div>
        )}
      </Popup>
    </section>
  );
};

export default AgentTemplateCard;
